import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { RevealText, Reveal } from "@/components/Reveal";
import { EDITING_QUEUE, REVIEW_LINKS } from "@/data/content";

export default function EditingQueue() {
  const q = EDITING_QUEUE;
  return (
    <div className="pt-8 md:pt-12">
      {/* Hero */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10">
        <p className="overline text-sage mb-6">Behind the scenes</p>
        <RevealText
          as="h1"
          className="font-serif font-light text-ink leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl"
          lines={["The editing", "queue"]}
        />
        <Reveal delay={0.1}>
          <p className="mt-10 max-w-2xl text-inksoft text-sm md:text-lg leading-relaxed">
            {q.intro}
          </p>
        </Reveal>
      </section>

      {/* Status card */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-20 md:mt-24">
        <Reveal>
          <div
            data-testid="editing-status-card"
            className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-4 border-t border-b border-ink/10 py-12 md:py-16"
          >
            <div>
              <p className="overline text-inkmuted mb-3">Current status</p>
              <p className="font-serif italic text-3xl md:text-4xl text-sage">
                {q.status}
              </p>
            </div>
            <div>
              <p className="overline text-inkmuted mb-3">Current turnaround</p>
              <p className="font-serif italic text-3xl md:text-4xl text-ink">
                {q.currentTurnaround}
              </p>
            </div>
            <div>
              <p className="overline text-inkmuted mb-3">Last updated</p>
              <p className="font-serif italic text-3xl md:text-4xl text-ink">
                {q.updatedOn}
              </p>
            </div>
          </div>
        </Reveal>
      </section>

      {/* Batches */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 mt-24 md:mt-32">
        <Reveal>
          <p className="overline text-sage mb-6">Where things stand</p>
          <h2 className="font-serif font-light text-ink text-4xl md:text-5xl leading-tight max-w-2xl">
            Which batch is your session in?
          </h2>
        </Reveal>
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-10">
          {q.batches.map((b, i) => (
            <Reveal key={b.label} delay={i * 0.08}>
              <div
                data-testid={`queue-batch-${i}`}
                className="border-l border-sage/40 pl-6 py-2"
              >
                <p className="overline text-sage mb-3">{b.label}</p>
                {b.date && b.name ? (
                  <>
                    <p className="text-xs tracking-[0.14em] uppercase text-inkmuted mb-2">
                      {b.date}
                    </p>
                    <p className="font-serif italic text-2xl text-ink mb-3">
                      {b.name}
                    </p>
                  </>
                ) : (
                  <p className="font-serif italic text-2xl text-ink mb-3">
                    {b.window}
                  </p>
                )}
                {b.sessions && b.sessions.length > 0 && (
                  <ul className="mb-3 space-y-1" data-testid={`queue-batch-${i}-sessions`}>
                    {b.sessions.map((name) => (
                      <li
                        key={name}
                        className="font-serif italic text-lg text-sage/90 leading-tight"
                      >
                        · {name}
                      </li>
                    ))}
                  </ul>
                )}
                {b.note && (
                  <p className="text-inksoft text-sm leading-relaxed">{b.note}</p>
                )}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Process */}
      <section className="bg-sagelight/40 mt-28 md:mt-40 py-24 md:py-32">
        <div className="mx-auto max-w-[1400px] px-6 md:px-10">
          <Reveal>
            <p className="overline text-sage mb-6">The process</p>
            <h2 className="font-serif font-light text-ink text-4xl md:text-6xl leading-tight max-w-3xl">
              From shutter to gallery
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
            {q.process.map((p, i) => (
              <Reveal key={p.step} delay={i * 0.06}>
                <div data-testid={`queue-process-${i}`}>
                  <p className="font-serif italic text-sage text-5xl mb-4">
                    {p.step}
                  </p>
                  <h3 className="font-serif text-2xl text-ink mb-3">
                    {p.title}
                  </h3>
                  <p className="text-inksoft text-sm leading-relaxed">
                    {p.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Footnote + contact */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-24 md:py-32">
        <Reveal>
          <p className="max-w-2xl text-inksoft italic text-lg leading-relaxed border-l-2 border-sage pl-6">
            {q.footnote}
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mt-16 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
            <h3 className="font-serif font-light text-ink text-3xl md:text-4xl leading-tight max-w-xl">
              Checking on your session?
            </h3>
            <Link
              to="/contact"
              data-testid="queue-contact-link"
              className="group inline-flex items-center gap-3 rounded-full bg-ink text-cream px-9 py-4 text-sm font-medium hover:bg-sagedark transition-colors"
            >
              Reach out
              <ArrowUpRight
                size={17}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
          </div>
        </Reveal>

        {/* Review nudge — small footer touch after gallery status */}
        <Reveal delay={0.2}>
          <div className="mt-24 md:mt-32 pt-12 border-t border-ink/10">
            <p className="max-w-2xl text-sm text-inksoft italic leading-relaxed">
              {REVIEW_LINKS.editingNote}
            </p>
            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 items-center">
              <a
                href={REVIEW_LINKS.google}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="review-google-queue"
                className="group inline-flex items-center gap-1.5 text-xs tracking-[0.14em] uppercase text-ink border-b border-ink/30 hover:border-sage hover:text-sage transition-colors pb-0.5"
              >
                Google
                <ArrowUpRight size={12} />
              </a>
              <a
                href={REVIEW_LINKS.facebook}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="review-facebook-queue"
                className="group inline-flex items-center gap-1.5 text-xs tracking-[0.14em] uppercase text-ink border-b border-ink/30 hover:border-sage hover:text-sage transition-colors pb-0.5"
              >
                Facebook
                <ArrowUpRight size={12} />
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
